import { Stack, Text, TextInput, Button } from "@mantine/core"
import { IconArrowRight } from "@tabler/icons-react"
import { useState, useEffect, useMemo } from "react"

import {
  AxesFeatureLabelSchema,
  LabelsExplorerResponses,
} from "@costory/types/endpoints/labelsExplorer"
import type { AxesFeatureLabel } from "@costory/types/endpoints/labelsExplorer"

interface NameLabelStepProps {
  selectedItem: string[]
  onConfirm: (data: AxesFeatureLabel) => void
  completed?: AxesFeatureLabel
  allLabels: LabelsExplorerResponses.LabelsStatsFullBilling[]
  mapping: {
    value: string
    to: string
  }[]
  editingLabel?: AxesFeatureLabel | null
}

export function NameLabelStep({
  selectedItem,
  onConfirm,
  completed,
  allLabels,
  mapping,
  editingLabel,
}: NameLabelStepProps) {
  const isGroup = selectedItem.length > 1

  const currentName = useMemo(() => {
    return selectedItem[0] || ""
  }, [selectedItem])

  const [newName, setNewName] = useState("")

  const validateName = (input: string) => {
    if (!input.trim()) return { isValid: false, error: "Name is required" }
    if (/[^a-zA-Z0-9_]/.test(input)) {
      return {
        isValid: false,
        error: "Only letters, numbers, and underscores are allowed",
      }
    }

    if (!/^[a-zA-Z_]/.test(input)) {
      return {
        isValid: false,
        error: "Name must start with a letter or underscore",
      }
    }

    const validationResult = AxesFeatureLabelSchema.shape.name.safeParse(input)
    if (!validationResult.success) {
      return {
        isValid: false,
        error:
          "Name must start with a letter or underscore and can only contain letters, numbers, and underscores",
      }
    }

    return { isValid: true, error: undefined }
  }

  useEffect(() => {
    if (completed?.name) {
      setNewName(completed.name)
    } else {
      setNewName(currentName)
    }
  }, [completed, currentName])

  const handleConfirm = () => {
    const validation = validateName(newName)
    if (!validation.isValid) return

    const finalName = newName.trim()

    if (isGroup) {
      // For group, we expect the merge step to have already created the columnsMerged
      // This step just sets the final name
      const axesFeatureLabel: AxesFeatureLabel = {
        name: finalName,
        columnsMerged: selectedItem.map((item) => ({
          name: item,
          type: "label" as const,
          ingested: false,
        })),
        maps: mapping,
      }
      onConfirm(axesFeatureLabel)
    } else if (selectedItem.length === 1) {
      const label = allLabels.find(
        (l: LabelsExplorerResponses.LabelsStatsFullBilling) =>
          l.key === selectedItem[0],
      )
      if (label) {
        const isIngest = finalName === label.key && mapping.length === 0
        const axesFeatureLabel: AxesFeatureLabel = {
          name: finalName,
          columnsMerged: [
            {
              name: label.key,
              type: "label" as const,
              ingested: isIngest,
            },
          ],
          maps: mapping,
        }
        onConfirm(axesFeatureLabel)
      }
    }
  }

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Choose the final name for your ingested label
      </Text>

      <TextInput
        label="Label Name"
        placeholder="Enter label name (letters, numbers, underscores only)"
        value={newName}
        onChange={(e) => setNewName(e.currentTarget.value)}
        size="sm"
        error={(() => {
          if (completed !== undefined && editingLabel === null) return undefined
          const validation = validateName(newName)
          return validation.isValid ? undefined : validation.error
        })()}
      />

      <Button
        size="sm"
        onClick={handleConfirm}
        disabled={!validateName(newName).isValid}
        rightSection={<IconArrowRight size={14} />}
      >
        {editingLabel !== null
          ? "Confirm & Continue"
          : completed !== undefined
            ? "Confirmed"
            : "Confirm & Continue"}
      </Button>
    </Stack>
  )
}
